import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { searchKnowledgeBase, Article, Category } from "@/hooks/useKnowledgeBase";

interface KnowledgeSearchBarProps {
  categories: Category[];
  onSelectArticle: (articleId: string) => void;
  className?: string;
}

export function KnowledgeSearchBar({ categories, onSelectArticle, className = "" }: KnowledgeSearchBarProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Article[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.length >= 2) {
      setResults(searchKnowledgeBase(categories, query));
      setIsOpen(true);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query, categories]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (articleId: string) => {
    onSelectArticle(articleId);
    setQuery("");
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar información..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
        />
        {query && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-gray-100"
            onClick={() => { setQuery(""); setResults([]); setIsOpen(false); }}
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 max-h-80 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="p-2">
            <p className="text-xs text-gray-400 px-2 pb-2">
              {results.length} resultado{results.length !== 1 ? "s" : ""} encontrado{results.length !== 1 ? "s" : ""}
            </p>
            {results.map((article) => (
              <button
                key={article.id}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors flex items-start gap-3"
                onClick={() => handleSelect(article.id)}
              >
                <article.icon className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{article.title}</p>
                  <p className="text-xs text-gray-500 truncate">{article.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {isOpen && query.length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="p-4 text-center text-gray-500 text-sm">
            No se encontraron resultados para &quot;{query}&quot;
          </div>
        </div>
      )}
    </div>
  );
}
